import { API } from "api";
import useSWR from "swr";
import { UTILS } from "utils";
import { CONSTANTS as C } from "config";
import { GET_CAREEM_STATUS , GET_JEEBLY_STATUS , GET_SLIDER_STATUS } from "api/api";
import { INTEGRATION_NAMES } from "logistic-integrations/constants";

const useOrder = () => {
  const userDetail = UTILS.getCurrentUser();
  const token = userDetail?.token;

  const isStreamRoomAccess = userDetail?.userType === "STREAM-ROOM-ACCESS";
  const fetcher = () =>
    API.GET_ORDER_COUNT({ token: token });

  const { data, error } = useSWR(
    isStreamRoomAccess ? null : token ? C.BASE_URL + "order/getAllPendingOrdersCount" : null,
    fetcher,
    {
      refreshInterval: 5000,
      revalidateOnFocus: true,
      refreshWhenHidden: true, // Allow polling even if tab is hidden
      refreshWhenOffline: true, // Allow polling even if the user is offline
      revalidateOnMount: true, // Always revalidate when the hook is mounted
      revalidateOnReconnect: true, // Revalidate when the user is back online
      revalidateOnWindowFocus: true,
    }
  );

  return {
    data: data,
    error: error,
  };
};

export default useOrder;

export const useGetLogisticStatus = ({
  orderId,
  status,
  integrationName,
}) => {
  const token = UTILS.getCurrentUser()?.token;
  const fn = {
    [INTEGRATION_NAMES.CAREEM_NOW]: GET_CAREEM_STATUS,
    [INTEGRATION_NAMES.JEEBLY]: GET_JEEBLY_STATUS,
    [INTEGRATION_NAMES.SLIDER]: GET_SLIDER_STATUS,
  };
  const apiFn = integrationName ? fn[integrationName] : null;
  const fetcher = () =>
    apiFn ? apiFn({ orderId, token }) : Promise.resolve(null);

  const key =
    orderId && token && apiFn
      ? `logistic-${orderId}-${integrationName}`
      : null;

  return useSWR(key, fetcher, {
    refreshInterval: status ? 30000 : 0,
    revalidateOnFocus: true,
    refreshWhenHidden: false,
    revalidateOnMount: true,
    revalidateOnReconnect: true,
  });
};

export const useGetCareemStatus = (orderId, status) => {
  const token = UTILS.getCurrentUser()?.token;
  const fetcher = () => GET_CAREEM_STATUS({ orderId, token })
  return useSWR((orderId && token && status) ? orderId : null, fetcher, {
    refreshInterval: status ? 30000 : (24 * 60 * 60 * 1000),
    revalidateOnFocus: true,
    refreshWhenHidden: false, // Allow polling even if tab is hidden
    refreshWhenOffline: true, // Allow polling even if the user is offline
    revalidateOnMount: true, // Always revalidate when the hook is mounted
    revalidateOnReconnect: true, // Revalidate when the user is back online
    revalidateOnWindowFocus: true,
  })
}

export const useGetJeeblyStatus = (orderId, status) => {
  const token = UTILS.getCurrentUser()?.token;
  const fetcher = () => GET_JEEBLY_STATUS({ orderId, token });

  return useSWR((orderId && token && status) ? `jeebly-${orderId}` : null, fetcher, {
    refreshInterval: status ? 30000 : (24 * 60 * 60 * 1000), // 30 sec or once a day
    revalidateOnFocus: true,
    refreshWhenHidden: false,
    refreshWhenOffline: true,
    revalidateOnMount: true,
    revalidateOnReconnect: true,
    revalidateOnWindowFocus: true,
  });
};

export const useGetSliderStatus = (orderId, status) => {
  const token = UTILS.getCurrentUser()?.token;
  const fetcher = () => GET_SLIDER_STATUS({ orderId, token });

  return useSWR((orderId && token && status) ? `slider-${orderId}` : null, fetcher, {
    refreshInterval: status ? 30000 : (24 * 60 * 60 * 1000), // 30 sec or once a day
    revalidateOnFocus: true,
    refreshWhenHidden: false,
    refreshWhenOffline: true,
    revalidateOnMount: true,
    revalidateOnReconnect: true,
    revalidateOnWindowFocus: true,
  });
};
